import React, { ChangeEvent, useRef } from "react";
import { Switch } from "antd";

import {
  PRODUCT_VISIBILITY_DESCRIPTIONS,
  PRODUCT_VISIBILITY_LABELS,
} from "../../components/ProductVisibility";
import FormSection from "./FormSection";
import Field from "./Field";
import { Upload } from "./icons";
import styles from "./fundForm.module.scss";

interface IVisibilitySection {
  isCustomerFacing: boolean;
  onVisibilityChange: (next: boolean) => void;
  termSheetName?: string;
  onPickTermSheet: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  step?: string;
}

/**
 * Who can see the fund, and the document that describes it.
 *
 * The toggle is not cosmetic: a product that is not customer-facing is
 * rejected when a customer tries to invest in it
 * (`CreateInvestmentValidator.cs:217`), so it is invisible and unbuyable.
 */
export default function VisibilitySection({
  isCustomerFacing,
  onVisibilityChange,
  termSheetName,
  onPickTermSheet,
  disabled = false,
  step,
}: IVisibilitySection) {
  const fileRef = useRef<HTMLInputElement | null>(null);

  return (
    <FormSection id="visibility" title="Visibility &amp; documents" meta={step}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div className={styles.toggleRow}>
          <div>
            <div className={styles.toggleTitle}>
              {isCustomerFacing
                ? PRODUCT_VISIBILITY_LABELS.visible
                : PRODUCT_VISIBILITY_LABELS.hidden}
            </div>
            <div className={styles.toggleCopy}>
              {isCustomerFacing
                ? PRODUCT_VISIBILITY_DESCRIPTIONS.visible
                : PRODUCT_VISIBILITY_DESCRIPTIONS.hidden}
            </div>
          </div>

          <div className={styles.toggleSide}>
            <span
              className={isCustomerFacing ? styles.badgeOk : styles.badgeMuted}
            >
              {isCustomerFacing ? "Visible" : "Hidden"}
            </span>
            <Switch
              checked={isCustomerFacing}
              disabled={disabled}
              onChange={onVisibilityChange}
              aria-label="Visible to customers"
            />
          </div>
        </div>

        <Field label="Supporting term sheet" optional>
          <button
            type="button"
            className={styles.dropzone}
            style={{ width: "100%" }}
            disabled={disabled}
            onClick={() => fileRef.current?.click()}
          >
            <span className={styles.dropIcon}>
              <Upload color="#713fff" />
            </span>
            <span className={styles.stack}>
              <span className={styles.dropTitle}>
                {termSheetName ? (
                  termSheetName
                ) : (
                  <>
                    Drop a file, or{" "}
                    <span className={styles.dropAccent}>browse</span>
                  </>
                )}
              </span>
              <span className={styles.dropMeta}>
                {termSheetName
                  ? "Click to replace"
                  : "PDF, PNG or JPG · up to 25 MB"}
              </span>
            </span>
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="image/png, image/jpg, image/jpeg, application/pdf"
            style={{ display: "none" }}
            onChange={onPickTermSheet}
          />
        </Field>
      </div>
    </FormSection>
  );
}
